import { Button, DialogActions, DialogTitle, Icon, Link, MenuItem } from "@material-ui/core";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { AssetType } from "common/constants";
import {
    ActionPermissionsObject,
    IWorkspaceResourceAsset,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";

import SharedAssetsPaneController, { SharedAssetsPaneControllerState } from "js/controllers/SharedAssetsPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { SearchAndAdd, SearchAndAddFilterOption } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildResourcePaneBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { Layout, PaneBody, PaneContent, ResourcePaneSideBar, SearchBarSort, teamResourceGridProps } from "js/react/views/SharedResources/Components";
import { LabeledCheckbox } from "js/react/views/SharedResources/Components/LabeledCheckbox";
import { WorkspaceResourceFolderHeaderActions } from "js/react/views/SharedResources/Components/WorkspaceResourceFolderMenu";

import Banner from "js/Components/Banner";
import { Button as AppButton } from "js/Components/Button";
import { Icon as BaiIcon } from "js/Components/Icon";
import UpgradeIcon from "js/Components/UpgradeIcon";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import aiGenerationService from "js/core/services/aiGeneration";
import { FileType } from "js/core/utilities/fileSourcesAndTypes";
import { getImageType } from "js/core/utilities/imageUtilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { BeautifulDialog, DialogContent, ShowConfirmationDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamAssetNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import AddAssetsContainer from "js/react/views/AddAssets/AddAssetsContainer";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import ImportFileDialog, { ImportFileDialogResult } from "js/react/views/ImportFile/ImportFileDialog";
import { MultiSelectThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { AssetThumbnail } from "js/react/views/SharedResources/Components/AssetThumbnail";
import { SharedResourceFolderNavigateHandler } from "js/react/views/SharedResources/Components/SharedResourceContextList";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { folderDropResourceFromWorkspaceResource, moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { buildTeamAssetMetadata } from "js/react/views/SharedResources/utils/buildTeamAssetMetadata";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import BulkActionBar, { BulkActionButton } from "js/react/views/SharedResources/Components/BulkActionBar";
import { BulkAssetUploadDialog } from "js/react/views/TeamResources/dialogs/BulkAssetUploadDialog";
import CreateTeamAssetDialog from "js/react/views/TeamResources/dialogs/CreateTeamAssetDialog";
import TeamAssetSettingsDialog from "js/react/views/TeamResources/dialogs/TeamAssetSettingsDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";
import { uuid } from "common/utils/uuid";
import { TemplatesListHeaderRow } from "js/editor/PresentationLibrary/libraryTemplatesPaneShared";

const logger = getLogger(LogGroup.TEAMS);
const TAG_FILTER_ALL = "__all__";

const ResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const BulkUploadButton = styled(AppButton)`
    &&& {
        letter-spacing: 0.5px;
        background-color: #ddd;
        color: #444;
        text-align: center;
        /* button/primary box */
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        border:none !important;
    }
`;

const SearchControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const AssetActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const CreateAssetButton = styled(AppButton)`
    &&& {
        letter-spacing: 0.5px;
    }
`;

type GenerateMetadataDialogProps = {
    selectionCount?: number;
    closeDialog: (shouldGenerate: boolean) => void;
    onGenerateAll: () => void;
};

function GenerateMetadataDialog({ selectionCount, closeDialog, onGenerateAll }: GenerateMetadataDialogProps) {
    const handleGenerateAll = () => {
        closeDialog(false);
        onGenerateAll();
    };

    return (
        <BeautifulDialog closeDialog={() => closeDialog(false)}>
            <DialogTitle>{t("Generate metadata?")}</DialogTitle>
            <DialogContent>
                <p>
                    {selectionCount === 1
                        ? t("Generate names, descriptions, and tags for 1 selected asset. This helps surface the right assets in search and when generating slides and presentations. Previously defined metadata will be preserved.")
                        : selectionCount
                            ? t("Generate names, descriptions, and tags for {{count}} selected assets. This helps surface the right assets in search and when generating slides and presentations. Previously defined metadata will be preserved.", { count: selectionCount })
                            : t("Generate names, descriptions, and tags for all assets. This helps surface the right assets in search and when generating slides and presentations. Previously defined metadata will be preserved.")}
                </p>
                {!!selectionCount && (
                    <p>
                        {t("To improve discoverability across your library,")}{" "}
                        <Link onClick={handleGenerateAll}>{t("generate metadata for all assets.")}</Link>
                    </p>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => closeDialog(false)}>{t("Cancel")}</Button>
                <Button color="primary" onClick={() => closeDialog(true)}>{t("Generate")}</Button>
            </DialogActions>
        </BeautifulDialog>
    );
}

type SharedAssetsPaneProps = WorkspaceControllerState & SharedAssetsPaneControllerState & {
    resourceType: string;
    editable?: boolean;
    addAssetCallback?: (asset: IWorkspaceResourceAsset) => void;
    handleConfirm?: () => void;
    onSelectFolder?: (pane: string, folderId?: string) => void;
    folderId?: string;
};

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: "sort_by_alpha",
}, {
    field: "resource.createdAt", label: t("Created At"), direction: "desc", icon: "calendar_month",
}, {
    field: "resource.modifiedAt", label: t("Modified At"), direction: "desc", icon: "edit_calendar",
}];

const ListHeaderBreadcrumbGroup = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;
`;

const ListHeaderControls = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
`;

function mediaFilterForResourceType(resourceType: string) {
    if (resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
        return "image";
    }
    if (resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
        return "logo";
    }
    return "__all__";
}

function SharedAssetsPane({
    resourceType: initialResourceType,
    editable,
    addAssetCallback,
    handleConfirm,
    tags,
    fetching,
    data,
    search,
    workspace,
    resourceFolders = [],
    onSelectFolder,
    folderId,
}: SharedAssetsPaneProps) {
    const [isInitializedForCurrentMount, setIsInitializedForCurrentMount] = useState(false);
    const [hasEverHadResults, setHasEverHadResults] = useState(false);
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
    const [sort, setSort] = useState<SearchBarSort>({
        field: "resource.modifiedAt",
        direction: "desc",
    });
    const [selectedTag, setSelectedTag] = useState(TAG_FILTER_ALL);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const [selectedItems, setSelectedItems] = useState<IWorkspaceResourceAsset[]>([]);

    const clearSelection = useCallback(() => {
        setSelectedItems([]);
    }, []);
    const [showHidden, setShowHidden] = useState(true);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems(folderDropResourceFromWorkspaceResource);
    const [resourceType, setResourceType] = useState<string>(initialResourceType);
    useEffect(() => {
        setResourceType(initialResourceType);
    }, [initialResourceType]);

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
    }, [folderId]);

    // Refetches clear data, so data.length alone can't tell an empty workspace from one that is
    // reloading. Once assets have been seen, the workspace is known not to be empty.
    useEffect(() => {
        if (data.length > 0) {
            setHasEverHadResults(true);
        }
    }, [data.length]);

    // initialize() resolves while the first page is still loading, so the pane is only done
    // loading once that first fetch settles.
    useEffect(() => {
        if (isInitializedForCurrentMount && !fetching) {
            setHasCompletedInitialLoad(true);
        }
    }, [isInitializedForCurrentMount, fetching]);

    const controllerSort = [{
        field: sort.field,
        direction: sort.direction,
    }];

    const controllerFilters = (() => {
        const filters = [];
        const mediaFilter = mediaFilterForResourceType(resourceType);

        if (mediaFilter === "__all__") {
            filters.push({
                field: "resource.metadata.type",
                value: ["image", "logo"],
                operator: "in",
            });
        } else {
            filters.push({
                field: "resource.metadata.type",
                value: mediaFilter,
                operator: "eq",
            });
        }

        if (selectedTag !== TAG_FILTER_ALL) {
            filters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            filters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        if (!editable || !showHidden) {
            filters.push({
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true,
            });
        }

        return filters;
    })();

    const tagFilterOptions = useMemo<SearchAndAddFilterOption[]>(() => [
        { field: TAG_FILTER_ALL, name: t("All Tags") },
        ...tags.map(({ tag, count }) => ({
            field: tag,
            name: tag,
            count,
        })),
    ], [tags]);

    useEffect(() => {
        let isMounted = true;

        void SharedAssetsPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            sort: controllerSort,
            filters: controllerFilters,
            search: { query: "" },
        }).then(() => {
            if (isMounted) {
                setIsInitializedForCurrentMount(true);
            }
        });

        return () => {
            isMounted = false;
            SharedAssetsPaneController.reset();
        };
    }, []);

    const prevSearchStateRef = useRef({
        resourceType,
        selectedTag,
        selectedFolderId,
        sort,
        showHidden,
    });

    useEffect(() => {
        const prev = prevSearchStateRef.current;

        if (!_.isEqual(prev.sort, sort)) {
            SharedAssetsPaneController.setSort(controllerSort);
        }
        if (
            prev.selectedTag !== selectedTag ||
            prev.selectedFolderId !== selectedFolderId ||
            prev.resourceType !== resourceType ||
            prev.showHidden !== showHidden
        ) {
            SharedAssetsPaneController.setFilters(controllerFilters);
            if (prev.selectedFolderId !== selectedFolderId && selectedFolderId) {
                SharedAssetsPaneController.setSearch({ query: "" });
            }
        }

        prevSearchStateRef.current = { resourceType, selectedTag, selectedFolderId, sort, showHidden };
    }, [resourceType, selectedTag, selectedFolderId, showHidden, getObjectHash(sort)]);

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canGenerateMetadata = WorkspaceController.actionPermissions?.[ActionPermissionsObject.GENERATIVE_AI]?.use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const existingTags = tags.map(tag => tag.tag);

    const createOrUpdateTeamAsset = async (
        fromAssetId: string,
        attributesOverrides: { description?: string, isPublished?: boolean, tags?: string[], name?: string, type?: string } = {},
        assetResource: IWorkspaceResourceAsset = null,
        isNewAsset: boolean = false,
        targetFolderId: string | null = null,
    ) => {
        if (assetResource) {
            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: assetResource.resourceId,
                    resourceType: WorkspaceResourceType.ASSET,
                    tags: attributesOverrides?.tags ?? assetResource?.tags ?? [],
                    metadata: buildTeamAssetMetadata({ fromAssetId, assetResource, attributesOverrides }),
                }, { isNewAsset, isReplacedAsset: fromAssetId !== assetResource.metadata.actualId });

                if (targetFolderId) {
                    await WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId: targetFolderId,
                        update: {
                            add: [{ resourceId: assetResource.resourceId, resourceType: WorkspaceResourceType.ASSET, permissions: { read: { object: true, permissions: true }, write: { object: false, permissions: false } } }],
                        },
                    });
                }

                const asset = await ds.assets.getAssetById(assetResource.resourceId);
                await asset.reload();
            } catch (error) {
                logger.error(error, "[SharedAssetsPane] Error updating workspace asset");
                throw error;
            }
            return;
        }

        try {
            const asset = await ds.assets.cloneAsset(fromAssetId, {
                userId: null,
                id: `${workspace.id}:${uuid()}`,
            }, false);

            if (!asset?.id) {
                throw new Error("Failed to clone asset: asset is missing or invalid.");
            }

            const createdResource = await WorkspaceController.createWorkspaceResource({
                resource: {
                    resourceId: asset.id,
                    resourceType: WorkspaceResourceType.ASSET,
                    folderIds: [],
                    tags: attributesOverrides?.tags || [],
                    metadata: buildTeamAssetMetadata({ fromAssetId, assetModel: asset, attributesOverrides }),
                },
                defaultPermissions: "shared",
            }, { isNewAsset, assetType: attributesOverrides?.type ?? asset.get("type") }) as IWorkspaceResourceAsset;

            if (targetFolderId) {
                await WorkspaceController.updateWorkspaceResourceFolderResources({
                    folderId: targetFolderId,
                    update: {
                        add: [{ resourceId: asset.id, resourceType: WorkspaceResourceType.ASSET, permissions: { read: { object: true, permissions: true }, write: { object: false, permissions: false } } }],
                    },
                });
            }

            await asset.reload();
            return createdResource;
        } catch (error) {
            logger.error(error, "[SharedAssetsPane] Error cloning and creating workspace asset");
            throw error;
        }
    };

    const generateMetadataForNewAsset = async (resourceId: string) => {
        try {
            return await withProgressDialog({
                title: t("Generating Team Asset metadata..."),
                message: t("This may take a moment."),
                action: () => aiGenerationService.generateTeamAssetMetadata({ resourceId }),
            });
        } catch (error) {
            logger.error(error, "[SharedAssetsPane] Failed to generate metadata for new asset", { resourceId });
            return null;
        }
    };

    const generateMetadataForNewAssets = async (resourceIds: string[]) => {
        try {
            await aiGenerationService.generateTeamAssetMetadataBackfill({ resourceIds });
        } catch (error) {
            logger.error(error, "[SharedAssetsPane] Failed to generate metadata for new assets", { resourceIds });
        }
    };

    const handleAddItem = async ({ createInFolder = false }: { createInFolder?: boolean } = {}) => {
        const { createdAssetModel, isNewAsset } = await new Promise<{
            createdAssetModel: { content_type: string, content_value: string, assetName: string, tags: string[] },
            isNewAsset: boolean,
        }>(resolve => {
            ShowDialog(AddAssetsContainer, {
                assetType: AssetType.IMAGE,
                hideTeamAssets: true,
                hideIcons: true,
                hideVideos: true,
                track: false,
                callback: (model: { content_type: string, content_value: string, assetName: string, tags: string[] }, isNew: boolean) => {
                    resolve({ createdAssetModel: model, isNewAsset: isNew });
                },
            });
        });

        if (!createdAssetModel) {
            return;
        }

        let pendingResource: IWorkspaceResourceAsset | null = null;
        try {
            if (canGenerateMetadata) {
                const createdResource = await createOrUpdateTeamAsset(
                    createdAssetModel.content_value,
                    {
                        isPublished: false,
                        name: createdAssetModel.assetName,
                        tags: createdAssetModel.tags,
                        type: createdAssetModel.content_type,
                    },
                    null,
                    isNewAsset,
                );
                if (!createdResource) {
                    throw new Error("Failed to create the pending Team Asset.");
                }
                pendingResource = createdResource;
            }

            const generatedMetadata = pendingResource
                ? await generateMetadataForNewAsset(pendingResource.resourceId)
                : null;
            const result = await ShowDialogAsync(CreateTeamAssetDialog, {
                type: generatedMetadata?.type ?? createdAssetModel.content_type,
                name: generatedMetadata?.name?.trim() || createdAssetModel.assetName,
                description: generatedMetadata?.description?.trim() || "",
                tags: generatedMetadata?.tags ?? createdAssetModel.tags,
                existingTags,
            }) as { type: string, name: string, description: string, tags: string[] } | undefined;

            if (!result) {
                if (pendingResource) {
                    await WorkspaceController.deleteWorkspaceResource({
                        resourceId: pendingResource.resourceId,
                        resourceType: WorkspaceResourceType.ASSET,
                    });
                }
                return;
            }

            const { type, name, description, tags: assetTags } = result;
            await createOrUpdateTeamAsset(
                createdAssetModel.content_value,
                { type, name, description, tags: assetTags, isPublished: true },
                pendingResource,
                isNewAsset,
                createInFolder ? selectedFolderId : null,
            );

            if (type === "logo" && resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
                handleSelectResourceType(TeamResourceTypes.SHARED_ASSETS_LOGOS);
            } else if (type === "image" && resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
                handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS_IMAGES);
            } else if (!createInFolder && selectedFolderId) {
                // Top-level create should leave the folder view so the new asset lands at root.
                setSelectedFolderId(null);
                onSelectFolder?.(resourceType, undefined);
            }
        } catch (err) {
            logger.error(err, "[SharedAssetsPane] Error creating team asset", { name: createdAssetModel.assetName });
            ShowErrorDialog({
                error: t("An error occurred while creating the team asset."),
                message: (err as Error).message,
            });
        }
    };

    const handleUpdateAsset = (teamAsset: IWorkspaceResourceAsset) =>
        new Promise(resolve => {
            const showAssetsDialog = () => {
                ShowDialog(AddAssetsContainer, {
                    assetType: teamAsset.metadata.type,
                    hideTeamAssets: true,
                    hideIcons: true,
                    hideVideos: true,
                    hidePhotos: teamAsset.metadata.type !== AssetType.IMAGE,
                    hideLogos: teamAsset.metadata.type !== AssetType.LOGO,
                    track: false,
                    onCancel: () => resolve(false),
                    callback: async (model: { content_type: string, content_value: string }, isNew: boolean) => {
                        try {
                            await createOrUpdateTeamAsset(model.content_value, { type: model.content_type }, teamAsset, isNew);
                            if (canGenerateMetadata && await ShowConfirmationDialog({
                                title: t("Review Asset Metadata"),
                                message: t("The asset image changed, but its existing metadata was preserved. Would you like to review or regenerate it now?"),
                                okButtonLabel: t("Review Metadata")
                            })) {
                                handleEditSettings({
                                    ...teamAsset,
                                    metadata: {
                                        ...teamAsset.metadata,
                                        actualId: model.content_value,
                                        type: model.content_type
                                    }
                                });
                            }
                            resolve(true);
                        } catch (err) {
                            logger.error(err, "[SharedAssetsPane] Error replacing asset", { id: teamAsset.resourceId });
                            ShowErrorDialog({
                                error: t("An error occurred while updating the asset."),
                                message: (err as Error).message,
                            });
                            resolve(false);
                        }
                    },
                });
            };

            ShowConfirmationDialog({
                title: t("Update Asset"),
                message: t("Update this asset once to update it everywhere it's used. Choose a replacement image or logo, and existing slides that use this asset will update automatically."),
                acceptCallback: showAssetsDialog,
                cancelCallback: () => resolve(false),
            });
        });

    const handleRemoveFromFolder = async (selection: IWorkspaceResourceAsset[]) => {
        if (!selectedFolderId) {
            return;
        }

        const resources = selection.map(asset => ({
            resourceId: asset.resourceId,
            resourceType: asset.resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: selection.length === 1 ? t("asset") : t("assets"),
        });
        clearSelection();
    };

    const handleDeleteAssets = async (selection: IWorkspaceResourceAsset[]) => {
        if (!selection.length) {
            return;
        }
        const isSingle = selection.length === 1;
        if (await ShowConfirmationDialog({
            title: isSingle
                ? t("Permanently delete this Team Asset?")
                : t("Permanently delete {{count}} selected Team Assets?", { count: selection.length }),
            message: t("This cannot be undone. Existing instances will remain unchanged, but will no longer receive updates."),
            buttonOptions: { variant: "outlined", style: { color: "#FF4500", borderColor: "transparent" } },
            okButtonLabel: t("DELETE"),
        })) {
            await withProgressDialog({
                title: selection.length === 1 ? t("Deleting Team Asset...") : t("Deleting {{count}} Team Assets...", { count: selection.length }),
                action: async () => {
                    for (const asset of selection) {
                        try {
                            await WorkspaceController.deleteWorkspaceResource({
                                resourceId: asset.resourceId,
                                resourceType: WorkspaceResourceType.ASSET,
                            });
                        } catch (err) {
                            logger.error(err, "[SharedAssetsPane] delete failed", { resourceId: asset.resourceId });
                            ShowErrorDialog({
                                error: t("An error occurred while deleting the Team Asset."),
                                message: (err as Error).message,
                            });
                        }
                    }
                },
            });
            clearSelection();
        }
    };

    const handleBulkPublish = useCallback(async (publish: boolean, targetItems?: IWorkspaceResourceAsset[]) => {
        const items = targetItems || selectedItems;
        if (!items.length) {
            return;
        }

        const isSingle = items.length === 1;

        if (publish) {
            const confirmed = await ShowConfirmationDialog({
                title: isSingle
                    ? t("Are you sure you want to publish this asset?")
                    : t("Are you sure you want to publish {{count}} selected assets?", { count: items.length }),
                message: isSingle
                    ? t("Publishing this asset allows it to be used by your team.")
                    : t("Publishing these {{count}} assets allows them to be used by your team.", { count: items.length }),
                okButtonLabel: isSingle ? t("Publish Asset") : t("Publish {{count}} Assets", { count: items.length }),
            });

            if (!confirmed) {
                return;
            }
        }

        const itemsToUpdate = items.filter(item => (publish ? item.metadata?.isPublished === false : item.metadata?.isPublished !== false));
        if (!itemsToUpdate.length) {
            clearSelection();
            return;
        }

        await withProgressDialog({
            title: publish
                ? (isSingle ? t("Publishing asset...") : t("Publishing {{count}} assets...", { count: items.length }))
                : (isSingle ? t("Unpublishing asset...") : t("Unpublishing {{count}} assets...", { count: items.length })),
            action: async () => {
                await WorkspaceController.updateWorkspaceResources(itemsToUpdate.map(item => ({
                    resourceId: item.resourceId,
                    resourceType: item.resourceType,
                    metadata: {
                        isPublished: publish,
                    },
                })));
            },
        });
        clearSelection();
    }, [clearSelection, selectedItems]);

    const handleEditSettings = async (assetResource: IWorkspaceResourceAsset) => {
        ShowDialog(TeamAssetSettingsDialog, {
            assetResource,
            existingTags,
        });
    };

    const handleBulkAddTags = useCallback(async (targetItems?: IWorkspaceResourceAsset[]) => {
        const items = targetItems || selectedItems;
        if (!items.length) {
            return;
        }

        const count = items.length;
        const subTitle = count === 1
            ? t("Add tags to this resource to help your team find it faster. Existing tags will not be removed.")
            : t("Add tags to {{count}} selected resources to help your team find them faster. Existing tags will not be removed.", { count });

        ShowDialog(AddTagsDialog, {
            activeTags: [],
            existingTags,
            subTitle,
            acceptCallback: async (newTags: string[]) => {
                if (!newTags.length) {
                    return;
                }
                await withProgressDialog({
                    title: t("Updating tags..."),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResources(items.map(asset => ({
                            resourceId: asset.resourceId,
                            resourceType: asset.resourceType,
                            tags: { add: newTags },
                        })));
                    },
                });
                clearSelection();
            },
        });
    }, [clearSelection, existingTags, selectedItems]);

    const handleBulkUpload = (files: File[]) => {
        const assetType = mediaFilterForResourceType(resourceType) === "logo"
            ? AssetType.LOGO
            : AssetType.IMAGE;

        const bulkUploadDialogProps = {
            files,
            existingTags,
            types: assetType,
            onAccept: async ({ tags: assetTags, types }: { tags: string[], types: string }) => {
                const progressDialog = ShowDialog(ProgressDialog, {
                    title: t("Uploading Assets..."),
                    progress: 0,
                });
                const failedFiles: string[] = [];
                const createdResourceIds: string[] = [];

                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    progressDialog.setProgress(i / files.length * 100);
                    progressDialog.setMessage(t("Uploading {{fileName}}", { fileName: file.name }));

                    const fileType = getImageType(file);
                    const currentType = types === AssetType.LOGO ? AssetType.LOGO : AssetType.IMAGE;
                    try {
                        const asset = await ds.assets.getOrCreateImage({
                            file,
                            name: file.name,
                            fileType,
                            assetType: currentType,
                            metadata: { source: "bulk-upload" },
                        });

                        const createdResource = await createOrUpdateTeamAsset(asset.id, { tags: assetTags, name: file.name, type: currentType }, null);
                        if (createdResource) {
                            createdResourceIds.push(createdResource.resourceId);
                        }
                    } catch (err) {
                        logger.error(err, "[SharedAssetsPane] Bulk upload failed for file", { name: file.name });
                        failedFiles.push(file.name);
                    }
                }

                if (canGenerateMetadata && createdResourceIds.length) {
                    progressDialog.setProgress(100);
                    progressDialog.setMessage(t("Generating metadata..."));
                    await generateMetadataForNewAssets(createdResourceIds);
                    await SharedAssetsPaneController.reload();
                }

                progressDialog.props.closeDialog();

                if (failedFiles.length > 0) {
                    const message = failedFiles.length === 1
                        ? t("Failed to upload {{fileName}}.", { fileName: failedFiles[0] })
                        : t("Failed to upload {{count}} files.", { count: failedFiles.length });

                    ShowErrorDialog({
                        error: t("Some assets could not be uploaded."),
                        message,
                    });
                }
            },
        };

        if (canGenerateMetadata) {
            void bulkUploadDialogProps.onAccept({ tags: [], types: assetType });
            return;
        }

        ShowDialog(BulkAssetUploadDialog, bulkUploadDialogProps);
    };

    const handleOpenBulkUpload = () => {
        ShowDialog(ImportFileDialog, {
            title: t("Bulk Upload"),
            fileTypes: [FileType.Image],
            multiple: true,
            closeOnSuccess: true,
            onSuccess: (result: ImportFileDialogResult) => {
                if (Array.isArray(result)) {
                    handleBulkUpload(result);
                }
            },
        });
    };

    const runMetadataBackfill = async (selection?: IWorkspaceResourceAsset[], skipConfirmation = false) => {
        const request = selection?.length
            ? { resourceIds: selection.map(asset => asset.resourceId) }
            : {};

        try {
            if (!skipConfirmation && !await ShowDialogAsync(GenerateMetadataDialog, {
                selectionCount: selection?.length,
                onGenerateAll: () => runMetadataBackfill(undefined, true),
            })) {
                return;
            }

            await withProgressDialog({
                title: t("Generating Team Asset metadata..."),
                message: t("This may take a moment."),
                action: () => aiGenerationService.generateTeamAssetMetadataBackfill(request)
            });
            await SharedAssetsPaneController.reload();
        } catch (error) {
            logger.error(error, "[SharedAssetsPane] Metadata backfill failed");
            ShowErrorDialog({
                title: t("Metadata generation failed"),
                message: t("Metadata could not be generated. Please try again.")
            });
        }
    };

    const renderContextMenu = (selection: IWorkspaceResourceAsset[]) => {
        if (!selection.length) {
            return null;
        }

        const isMulti = selection.length > 1;

        if (isMulti) {
            const hasSelectionUnpublished = selection.some(item => item.metadata?.isPublished === false);
            const hasSelectionPublished = selection.some(item => item.metadata?.isPublished !== false);

            return [
                <MenuItem
                    key="tags"
                    divider={!canGenerateMetadata}
                    onClick={() => void handleBulkAddTags(selection)}
                >
                    <Icon>label</Icon>{t("Add Tags…")}
                </MenuItem>,
                ...(canGenerateMetadata ? [
                    <MenuItem
                        key="generate-metadata"
                        divider
                        onClick={() => runMetadataBackfill(selection)}
                    >
                        <Icon>auto_awesome</Icon>{t("Generate Metadata…")}
                    </MenuItem>
                ] : []),
                ...(hasSelectionUnpublished ? [
                    <MenuItem
                        key="publish"
                        divider={!hasSelectionPublished}
                        onClick={() => void handleBulkPublish(true, selection)}
                    >
                        <Icon>cloud_upload</Icon>{t("Publish")}
                    </MenuItem>
                ] : []),
                ...(hasSelectionPublished ? [
                    <MenuItem
                        key="unpublish"
                        divider
                        onClick={() => void handleBulkPublish(false, selection)}
                    >
                        <Icon>unpublished</Icon>{t("Unpublish")}
                    </MenuItem>
                ] : []),
                ...(selectedFolderId ? [
                    <MenuItem
                        key="remove-from-folder"
                        divider
                        onClick={() => void handleRemoveFromFolder(selection)}
                    >
                        <Icon>remove_circle</Icon>{t("Remove from Folder")}
                    </MenuItem>
                ] : []),
                <MenuItem
                    key="trash"
                    onClick={() => void handleDeleteAssets(selection)}
                >
                    <Icon>delete</Icon>{t("Delete")}
                </MenuItem>,
            ];
        }

        const item = selection[0];
        const isPublished = item.metadata?.isPublished !== false;

        if (canUpgradeToManageSharedResources) {
            return [
                <MenuItem key="replace" disabled><UpgradeIcon /><Gap5 />{t("Replace Asset…")}</MenuItem>,
                <MenuItem key="edit" disabled><UpgradeIcon /><Gap5 />{t("Properties…")}</MenuItem>,
                <MenuItem key="tags" disabled divider><UpgradeIcon /><Gap5 />{t("Add Tags…")}</MenuItem>,
                isPublished ? (
                    <MenuItem key="unpublish" disabled divider><UpgradeIcon /><Gap5 />{t("Unpublish")}</MenuItem>
                ) : (
                    <MenuItem key="publish" disabled divider><UpgradeIcon /><Gap5 />{t("Publish")}</MenuItem>
                ),
                ...(selectedFolderId ? [
                    <MenuItem key="remove-from-folder" disabled divider><UpgradeIcon /><Gap5 />{t("Remove from Folder")}</MenuItem>
                ] : []),
                <MenuItem key="trash" disabled><UpgradeIcon /><Gap5 />{t("Delete")}</MenuItem>
            ];
        }

        return [
            <MenuItem key="replace" onClick={() => handleUpdateAsset(item)}>
                <Icon>sync</Icon>{t("Replace Asset…")}
            </MenuItem>,
            <MenuItem key="edit" onClick={() => handleEditSettings(item)}>
                <Icon>edit</Icon>{t("Properties…")}
            </MenuItem>,
            <MenuItem
                key="tags"
                divider={!canGenerateMetadata}
                onClick={() => void handleBulkAddTags([item])}
            >
                <Icon>label</Icon>{t("Add Tags…")}
            </MenuItem>,
            ...(canGenerateMetadata ? [
                <MenuItem
                    key="generate-metadata"
                    divider
                    onClick={() => runMetadataBackfill([item])}
                >
                    <Icon>auto_awesome</Icon>{t("Generate Metadata…")}
                </MenuItem>
            ] : []),
            isPublished ? (
                <MenuItem key="unpublish" divider onClick={() => handleBulkPublish(false, [item])}>
                    <Icon>unpublished</Icon>{t("Unpublish")}
                </MenuItem>
            ) : (
                <MenuItem key="publish" divider onClick={() => handleBulkPublish(true, [item])}>
                    <Icon>cloud_upload</Icon>{t("Publish")}
                </MenuItem>
            ),
            ...(selectedFolderId ? [
                <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveFromFolder([item])}>
                    <Icon>remove_circle</Icon>{t("Remove from Folder")}
                </MenuItem>
            ] : []),
            <MenuItem key="trash" onClick={() => handleDeleteAssets([item])}>
                <Icon>delete</Icon>{t("Delete")}
            </MenuItem>,
        ];
    };

    const handleFolderDrop = useCallback(async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            onSelectFolder?.(resourceType, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving asset to folder..."),
            progressTitlePlural: count => t("Moving {{count}} assets to folder...", { count }),
            errorMessage: t("An error occurred while moving assets to the folder."),
            logTag: "SharedAssetsPane"
        });
    }, [clearDragItems, getActiveDragItems, onSelectFolder, resourceType, selectedFolderId]);

    const handleThumbnailVisibilityChange = (isVisible: boolean, item: IWorkspaceResourceAsset) => {
        if (!isVisible || !SharedAssetsPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(({ resource }) => resource.id === item.id);
        if (index === data.length - 1) {
            SharedAssetsPaneController.loadMore();
        }
    };

    const handleSelectResourceType = useCallback((nextResourceType: string) => {
        setResourceType(nextResourceType);
        onSelectFolder?.(nextResourceType, undefined);
        setSelectedFolderId(null);
        if (nextResourceType !== TeamResourceTypes.TEAM_ASSETS) {
            SharedAssetsPaneController.setSearch({ query: "" });
        }
    }, [onSelectFolder]);

    const handleNavigateToFolder: SharedResourceFolderNavigateHandler = useCallback(({ folderId: nextFolderId, parentFolderId }) => {
        const nextSelectedFolderId = parentFolderId ?? nextFolderId;
        setSelectedFolderId(nextSelectedFolderId);
        setResourceType(TeamResourceTypes.TEAM_ASSETS);
        SharedAssetsPaneController.setSearch({ query: "" });
        onSelectFolder?.(TeamResourceTypes.TEAM_ASSETS, nextSelectedFolderId);
    }, [onSelectFolder]);

    const handleSearch = (searchTerm: string) => {
        SharedAssetsPaneController.setSearch({ query: searchTerm?.trim().toLowerCase() });
        handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS);
    };

    const handleSort = (nextSort: SearchBarSort) => {
        setSort(nextSort);
    };

    const handleSelectTag = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
    };

    const handleSelectSidebarFolder = useCallback((nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        SharedAssetsPaneController.setSearch({ query: "" });
        setResourceType(TeamResourceTypes.TEAM_ASSETS);
        onSelectFolder?.(TeamResourceTypes.TEAM_ASSETS, nextFolderId ?? undefined);
    }, [onSelectFolder]);

    const filteredAssets = data.map(record => record.resource);

    useEffect(() => {
        setSelectedItems(current => current.filter(item => filteredAssets.some(asset => asset.id === item.id)));
    }, [getObjectHash(filteredAssets)]);

    const hasPublished = selectedItems.some(item => item.metadata?.isPublished !== false);
    const hasUnpublished = selectedItems.some(item => item.metadata?.isPublished === false);

    const shouldHideTopLevelSideBar = filteredAssets.length === 0 &&
        !hasEverHadResults &&
        !search.query &&
        !selectedFolderId &&
        selectedTag === TAG_FILTER_ALL &&
        resourceType === TeamResourceTypes.TEAM_ASSETS;
    // Sidebar and header both wait for the first fetch to settle so the loading spinner stands
    // alone and the pane chrome appears in one step instead of piece by piece.
    const shouldRenderSideBar = hasCompletedInitialLoad && !shouldHideTopLevelSideBar;
    const shouldHideTopLevelHeader = !hasCompletedInitialLoad;

    const sideBarCategoryNav = useMemo(() => (
        <>
            <SideBarItem
                className={selectedClass(resourceType === TeamResourceTypes.TEAM_ASSETS && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS)}
                id="all-assets"
            >
                <Icon className="material-symbols-outlined">select_all</Icon>
                {t("All assets")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS_IMAGES)}
                id="asset-photos"
            >
                <Icon className="material-symbols-outlined">photo_camera</Icon>
                {t("Photos")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(TeamResourceTypes.SHARED_ASSETS_LOGOS)}
                id="asset-logos"
            >
                <Icon className="material-symbols-outlined">sort_by_alpha</Icon>
                {t("Logos")}
            </SideBarItem>
        </>
    ), [handleSelectResourceType, resourceType, selectedFolderId]);

    const renderAssets = () => {
        const isLoadingMore = fetching && filteredAssets.length > 0;
        const isLoadingInitial = fetching && filteredAssets.length === 0;

        if (isLoadingInitial || !SharedAssetsPaneController.initialized) {
            return <Spinner />;
        }

        if (!filteredAssets.length) {
            if (selectedFolderId) {
                return (<SharedSlideFolderNotice hideMessage={!editable} />);
            }

            if (
                !search.query &&
                !selectedFolderId &&
                selectedTag === TAG_FILTER_ALL
            ) {
                if (resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
                    return (<SharedSlideFolderNotice
                        title={t("You don’t have any images yet.")}
                        message={t("Create a library of shared assets to keep your team on-brand and up to date. Updates will apply anywhere it's used.")}
                    />);
                }
                if (resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
                    return (<SharedSlideFolderNotice
                        title={t("You don’t have any logos yet.")}
                        message={t("Create a library of shared assets to keep your team on-brand and up to date. Updates will apply anywhere it's used.")}
                    />);
                }
                return <NoTeamAssetNotice />;
            }
            return <NoMatchNotice />;
        }

        return (
            <MultiSelectThumbnailGrid
                {...teamResourceGridProps}
                items={filteredAssets}
                selection={selectedItems.map(item => item.id)}
                readOnly={!editable}
                allowDragging={editable && canUseSharedResources}
                allowMultiSelect={editable && canUseSharedResources}
                thumbnailClass={AssetThumbnail}
                thumbnailProps={{
                    canUpgradeToManageSharedResources,
                    onVisibleChange: handleThumbnailVisibilityChange,
                    resourceFolders,
                    onNavigateToFolder: handleNavigateToFolder,
                }}
                onStartDragItems={handleStartDragItems}
                onDropDragItems={handleDropDragItems}
                onItemSelected={async selection => {
                    setSelectedItems(selection);
                    if (canUpgradeToManageSharedResources) {
                        return;
                    }
                    if (addAssetCallback && selection.length > 0) {
                        try {
                            await addAssetCallback(selection[0]);
                        } catch (err) {
                            logger.error(err, "[SharedAssetsPane] addAssetCallback() failed");
                        }
                        handleConfirm?.();
                    }
                }}
                onDoubleClick={
                    canUpgradeToManageSharedResources
                        ? undefined
                        : addAssetCallback
                            ? async asset => {
                                try {
                                    await addAssetCallback(asset);
                                } catch (err) {
                                    logger.error(err, "[SharedAssetsPane] addAssetCallback() failed");
                                }
                                handleConfirm?.();
                            }
                            : asset => handleEditSettings(asset)
                }
                renderContextMenuItems={editable ? renderContextMenu : undefined}
                isLoadingMore={isLoadingMore}
            />
        );
    };

    const title = resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES
        ? t("Photos")
        : resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS
            ? t("Logos")
            : t("Assets");

    const handleBreadcrumbSelectFolder = (nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        SharedAssetsPaneController.setSearch({ query: "" });
        setResourceType(TeamResourceTypes.TEAM_ASSETS);
        onSelectFolder?.(TeamResourceTypes.TEAM_ASSETS, nextFolderId ?? undefined);
    };

    const breadcrumb = useMemo(() => buildResourcePaneBreadcrumbData({
        rootLabel: title,
        selectedFolderId,
        resourceFolders,
        onSelectFolder: handleBreadcrumbSelectFolder,
    }), [title, selectedFolderId, resourceFolders]);

    return (
        <Layout>
            {canUseSharedResources && !shouldHideTopLevelHeader && (<>
                <SearchControls>
                    <SearchAndAdd
                        onSearch={handleSearch}
                        searchValue={search.query}
                        searchPlaceholder={t("Search team assets...")}
                        tagFilterOptions={tagFilterOptions}
                        selectedTagFilter={selectedTag}
                        onTagFilterChange={handleSelectTag}
                    />
                    {editable && (
                        <AssetActions>
                            <BulkUploadButton
                                iconColor="#666"
                                large
                                onClick={handleOpenBulkUpload}
                            >
                                <BaiIcon>arrow_circle_up</BaiIcon>
                                {t("Bulk Upload")}
                            </BulkUploadButton>
                            <CreateAssetButton blue large onClick={() => handleAddItem()}>
                                <BaiIcon white fill>add_circle</BaiIcon>
                                {t("New Asset")}
                            </CreateAssetButton>
                        </AssetActions>
                    )}
                </SearchControls>
            </>)}

            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Team Assets")}
                        bodyText={t("Upgrade to a Team plan to centralize approved brand assets.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "unlock_team_assets" }
                            })}
                    />
                </FlexBox>
            )}

            <PaneBody>
                <ResourcePaneSideBar
                    visible={shouldRenderSideBar}
                    readOnly={!editable}
                    resourceTypes={[WorkspaceResourceType.ASSET]}
                    folderType={WorkspaceResourceFolderType.SHARED}
                    resourceFolders={resourceFolders}
                    selectedFolderId={selectedFolderId}
                    draggedItems={draggedItems}
                    onFolderDrop={handleFolderDrop}
                    onSelectFolder={handleSelectSidebarFolder}
                >
                    {sideBarCategoryNav}
                </ResourcePaneSideBar>
                <PaneContent>
                    {!shouldHideTopLevelHeader && (
                        <TemplatesListHeaderRow>
                            <ListHeaderBreadcrumbGroup>
                                <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                                {editable && selectedFolderId && (
                                    <WorkspaceResourceFolderHeaderActions
                                        folderId={selectedFolderId}
                                        resourceTypes={[WorkspaceResourceType.ASSET]}
                                        resourceFolders={resourceFolders}
                                        onSelectFolder={handleSelectSidebarFolder}
                                        onCreateItem={() => handleAddItem({ createInFolder: true })}
                                        showCreateButton
                                    />
                                )}
                            </ListHeaderBreadcrumbGroup>
                            <ListHeaderControls>
                                {editable && (
                                    <LabeledCheckbox
                                        checked={showHidden}
                                        label={t("Show hidden assets")}
                                        onChange={setShowHidden}
                                    />
                                )}
                                {canUseSharedResources && (
                                    <PresentationListHeader
                                        sortOptions={sortOptions}
                                        sort={sort}
                                        searchQuery={search.query ?? ""}
                                        onSetSort={handleSort}
                                        showViewTypeToggle={false}
                                    />
                                )}
                            </ListHeaderControls>
                        </TemplatesListHeaderRow>
                    )}
                    <ResultsContainer>
                        {renderAssets()}
                    </ResultsContainer>
                </PaneContent>
                {editable && selectedItems.length > 0 && (
                    <BulkActionBar
                        selectedCount={selectedItems.length}
                        onDelete={() => handleDeleteAssets(selectedItems)}
                        onClearSelection={clearSelection}
                    >
                        <BulkActionButton
                            data-testid="bulk-action-add-tags"
                            icon="label"
                            onClick={() => handleBulkAddTags()}
                        >
                            {t("Add Tags…")}
                        </BulkActionButton>

                        {canGenerateMetadata && (
                            <BulkActionButton
                                data-testid="bulk-action-generate-metadata"
                                icon="auto_awesome"
                                onClick={() => runMetadataBackfill(selectedItems)}
                            >
                                {t("Generate Metadata…")}
                            </BulkActionButton>
                        )}

                        {hasUnpublished && (
                            <BulkActionButton
                                data-testid="bulk-action-publish"
                                icon="check_circle"
                                onClick={() => handleBulkPublish(true)}
                            >
                                {t("Publish")}
                            </BulkActionButton>
                        )}

                        {hasPublished && (
                            <BulkActionButton
                                data-testid="bulk-action-unpublish"
                                icon="unpublished"
                                onClick={() => handleBulkPublish(false)}
                            >
                                {t("Unpublish")}
                            </BulkActionButton>
                        )}
                    </BulkActionBar>
                )}
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(SharedAssetsPaneController.withState(SharedAssetsPane));
